import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Alerte persistante de changement de segment tarifaire (HTA / C4 / C5).
 *
 * Affichée dans les vues rapport (web) lorsque la formule proposée appartient
 * à un domaine de tension différent de la formule actuelle.
 *
 * IMPORTANT (choix utilisateur) : on indique qu'un changement de segment PEUT
 * entraîner des frais ENEDIS, SANS jamais s'engager sur un montant — les frais
 * dépendent des travaux réels et nécessitent un devis ENEDIS officiel.
 */
const SEGMENT_LABELS = {
  HTA: 'HTA (C2 / C3)',
  BTSUP: 'BT > 36 kVA (C4)',
  BTINF: 'BT ≤ 36 kVA (C5)',
};

const INTERVENTION = {
  'HTA→BTSUP': 'Pose ou remplacement de compteur + adaptation du comptage',
  'HTA→BTINF': "Pose d'un compteur + abaissement de puissance — vérifier le raccordement existant",
  'BTSUP→HTA': 'Raccordement HTA (transformateur, cellule, comptage HTA) — travaux potentiellement lourds',
  'BTSUP→BTINF': 'Changement de compteur et de disjoncteur — puissance cible ≤ 36 kVA',
  'BTINF→HTA': 'Raccordement HTA — travaux potentiellement lourds',
  'BTINF→BTSUP': 'Changement de compteur et augmentation du disjoncteur de branchement',
};

export const tensionFromFormule = (formule) => {
  if (!formule) return null;
  const f = String(formule).toUpperCase();
  if (f.startsWith('HTA')) return 'HTA';
  if (f.startsWith('BTSUP')) return 'BTSUP';
  if (f.startsWith('BTINF')) return 'BTINF';
  return null;
};

export const SegmentChangeAlert = ({ formuleActuelle, formuleProposee, tensionActuelle, compact = false }) => {
  const segActuel = tensionFromFormule(formuleActuelle) || tensionActuelle || null;
  const segPropose = tensionFromFormule(formuleProposee);

  // Pas de changement de segment → rien à afficher
  if (!segActuel || !segPropose || segActuel === segPropose) return null;

  const cle = `${segActuel}→${segPropose}`;
  const intervention = INTERVENTION[cle];

  return (
    <div
      data-testid="segment-change-alert"
      className={`flex items-start gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 ${compact ? 'p-3' : 'p-4'} text-amber-900`}
    >
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
      <div className={compact ? 'text-xs' : 'text-sm'}>
        <p className="font-semibold">
          Changement de segment tarifaire&nbsp;:&nbsp;
          {SEGMENT_LABELS[segActuel] || segActuel} → {SEGMENT_LABELS[segPropose] || segPropose}
        </p>
        <p className="mt-1">
          Ce changement de segment peut entraîner des <strong>frais ENEDIS</strong> spécifiques
          {intervention ? ` (${intervention.toLowerCase()})` : ''}. Ces coûts sont distincts d'une simple
          modification de puissance souscrite, <strong>dépendent des travaux à réaliser</strong> et ne sont
          pas inclus dans les économies affichées.
        </p>
        <p className="mt-1 font-medium">
          Un devis ENEDIS officiel est nécessaire avant tout engagement.
        </p>
      </div>
    </div>
  );
};

export default SegmentChangeAlert;
